import React, { useEffect, useState } from "react";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import { Link } from "react-router-dom";
import { 
  Plus, Edit, Trash2, CheckCircle, Clock, MapPin, Calendar, 
  DollarSign, Search, Globe, X, Download, Users, Info, Sparkles, Filter 
} from "lucide-react";
import { toast } from "react-hot-toast";

const PERSONALITY_VIBES = {
  "All": [],
  "The Nurturer (🌿 Healing)": ["Healing", "Wellness", "Brunch"],
  "The Go-Getter (🔥 Growth)": ["Networking", "Growth", "Workshop"],
  "The Socialite (🦋 Social)": ["Social", "Party", "Festival"],
  "The Deep Soul (🌿 Healing)": ["Meditation", "Deep Talk"],
  "The Soft Life Curator (🌸 Soft Life)": ["Spa", "Calm", "Soft Life"],
  "The Explorer (🦋 Social)": ["Adventure", "Outdoor"],
  "The Visionary (🔥 Growth)": ["Creative", "Workshop"],
  "The Grounded One (🌿 Healing)": ["Nature", "Wellness"]
};

const EventManager = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExternal, setShowExternal] = useState(false);
  const [externalEvents, setExternalEvents] = useState([]);
  const [fetchingExternal, setFetchingExternal] = useState(false);
  const [searchLocation, setSearchLocation] = useState("New York");
  const [searchCategory, setSearchCategory] = useState("social");
  const [searchPrice, setSearchPrice] = useState("All");
  const [searchType, setSearchType] = useState("All");
  const [searchSource, setSearchSource] = useState("All");
  const [activeDiscoveryTab, setActiveDiscoveryTab] = useState("Eventbrite");
  const [selectedExternal, setSelectedExternal] = useState([]);
  const [isBulkPublishing, setIsBulkPublishing] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [personalityFilter, setPersonalityFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [showAlgoInfo, setShowAlgoInfo] = useState(false);
  const [activeListTab, setActiveListTab] = useState("Upcoming");

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await api.get(URLS.GET_EVENTS);
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(e => {
    const matchesSearch = e.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          (e.locationName && e.locationName.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === "All" || e.status === statusFilter;
    
    let matchesSource = true;
    if (sourceFilter !== "All") {
        if (sourceFilter === "Ticketmaster") matchesSource = e.source === "Ticketmaster";
        else if (sourceFilter === "Eventbrite") matchesSource = e.source === "Eventbrite";
        else matchesSource = !e.source || e.source === "Tribe25";
    }
    
    let matchesPersonality = true;
    if (personalityFilter !== "All") {
        const requiredVibes = PERSONALITY_VIBES[personalityFilter];
        matchesPersonality = (e.vibeTags || []).some(tag => 
            requiredVibes.some(rv => tag.toLowerCase().includes(rv.toLowerCase()))
        );
    }

    const start = new Date(e.eventStartAt);
    const end = e.eventEndAt ? new Date(e.eventEndAt) : new Date(start.getTime() + 3 * 60 * 60 * 1000);
    const now = new Date();

    let tabCategory = "History";
    if (now < start) {
        tabCategory = "Upcoming";
    } else if (now >= start && now <= end) {
        tabCategory = "Ongoing";
    }

    return matchesSearch && matchesStatus && matchesPersonality && matchesSource && activeListTab === tabCategory;
  });

  const finalSortedEvents = [...filteredEvents].sort((a, b) => {
    const dateA = new Date(a.eventStartAt);
    const dateB = new Date(b.eventStartAt);
    if (activeListTab === "History") {
      return dateB - dateA; // Most recent past event first
    }
    return dateA - dateB; // Soonest upcoming/ongoing first
  });

  const fetchExternal = async (sourceOverride) => {
    const sourceToUse = sourceOverride || searchSource;
    setFetchingExternal(true);
    try {
      const res = await api.get(`${URLS.GET_EXTERNAL_EVENTS}?location=${searchLocation}&category=${searchCategory}&isFree=${searchPrice === 'Free'}&eventType=${searchType}&source=${sourceToUse}`);
      
      // If we are fetching a specific source, we might want to merge or replace.
      // For simplicity in this UX, we'll replace the existing cached external events for that source
      const newEvents = res.data.events || [];
      setExternalEvents(prev => {
          const filtered = prev.filter(e => e.source !== sourceToUse);
          return [...filtered, ...newEvents];
      });
      
      if (newEvents.length === 0) toast.error(`No ${sourceToUse} events found for this criteria`);
    } catch (err) {
      toast.error(`Failed to fetch ${sourceToUse} events`);
    } finally {
      setFetchingExternal(false);
    }
  };

  const isAlreadyPublished = (extEvent) => {
    return events.some(e => e.externalId === extEvent.externalId && e.source === extEvent.source);
  };

  const handleImport = async (extEvent) => {
    try {
      const eventData = {
        externalId: extEvent.externalId,
        title: extEvent.title,
        description: extEvent.description,
        coverImageUrl: extEvent.coverImageUrl,
        eventStartAt: extEvent.eventStartAt,
        eventEndAt: extEvent.eventEndAt,
        locationName: extEvent.locationName,
        externalTicketUrl: extEvent.url,
        price: extEvent.price || 0,
        eventType: extEvent.eventType,
        status: "Published",
        source: extEvent.source,
        vibeTags: extEvent.suggestedVibeTags || ["Social"],
        capacity: extEvent.capacity || 0,
        totalSold: extEvent.totalSold || 0
      };
      await api.post(URLS.CREATE_EVENT, eventData);
      toast.success(isAlreadyPublished(extEvent) ? "Event updated! 🔄" : "Event imported and published! ✨");
      fetchEvents();
    } catch (err) {
      toast.error("Failed to import event");
    }
  };

  const handleBulkPublish = async () => {
    if (selectedExternal.length === 0) return;
    
    setIsBulkPublishing(true);
    try {
        const eventsToPublish = selectedExternal.map(extEvent => ({
            externalId: extEvent.externalId,
            title: extEvent.title,
            description: extEvent.description,
            coverImageUrl: extEvent.coverImageUrl,
            eventStartAt: extEvent.eventStartAt,
            eventEndAt: extEvent.eventEndAt,
            locationName: extEvent.locationName,
            externalTicketUrl: extEvent.url,
            price: extEvent.price || 0,
            eventType: extEvent.eventType,
            source: extEvent.source,
            vibeTags: extEvent.suggestedVibeTags || ["Social"],
            capacity: extEvent.capacity || 0,
            totalSold: extEvent.totalSold || 0
        }));

        await api.post("/api/admin-events/bulk-publish", { events: eventsToPublish });
        toast.success(`Successfully processed ${selectedExternal.length} events! 🚀`);
        setSelectedExternal([]);
        setShowExternal(false);
        fetchEvents();
    } catch (err) {
        toast.error("Failed to bulk publish events");
    } finally {
        setIsBulkPublishing(false);
    }
  };

  const toggleExternalSelection = (event) => {
    const isSelected = selectedExternal.find(e => e.externalId === event.externalId);
    if (isSelected) {
        setSelectedExternal(selectedExternal.filter(e => e.externalId !== event.externalId));
    } else {
        setSelectedExternal([...selectedExternal, event]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await api.delete(URLS.DELETE_EVENT(id));
      toast.success("Event deleted");
      fetchEvents();
    } catch (err) {
      toast.error("Failed to delete event");
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.post(URLS.APPROVE_EVENT(id));
      toast.success("Event approved and published!");
      fetchEvents();
    } catch (err) {
      toast.error("Failed to approve event");
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading events...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Event Management</h2>
          <p className="text-gray-500">Manage curated and community-suggested events</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowAlgoInfo(!showAlgoInfo)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition shadow-md w-fit border ${showAlgoInfo ? 'bg-orange-50 border-orange-200 text-[#db8654]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            <Sparkles size={18} />
            Algorithm Guide
          </button>
          <button
            onClick={() => {
              setSearchSource("Eventbrite");
              setActiveDiscoveryTab("Eventbrite");
              setShowExternal(true);
              if (externalEvents.filter(e => e.source === "Eventbrite").length === 0) fetchExternal("Eventbrite");
            }}
            className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-xl hover:bg-orange-700 transition shadow-md w-fit"
          >
            <Globe size={18} />
            Eventbrite
          </button>
          <button
            onClick={() => {
              setSearchSource("Ticketmaster");
              setActiveDiscoveryTab("Ticketmaster");
              setShowExternal(true);
              if (externalEvents.filter(e => e.source === "Ticketmaster").length === 0) fetchExternal("Ticketmaster");
            }}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl hover:bg-indigo-700 transition shadow-md w-fit"
          >
            <Globe size={18} />
            Ticketmaster
          </button>
          <Link
            to="/admin/events/create"
            className="flex items-center gap-2 bg-[#db8654] text-white px-4 py-2 rounded-xl hover:bg-[#c47548] transition shadow-md w-fit"
          >
            <Plus size={20} />
            Create Event
          </Link>
        </div>
      </div>

      {showAlgoInfo && (
          <div className="bg-gradient-to-r from-[#db8654]/10 to-orange-50 p-6 rounded-3xl border border-[#db8654]/20 animate-in fade-in slide-in-from-top-4">
              <div className="flex items-start gap-4">
                  <div className="bg-[#db8654] p-3 rounded-2xl text-white">
                      <Info size={24} />
                  </div>
                  <div>
                      <h3 className="text-lg font-bold text-gray-800 mb-2">How "For You" Matching Works 🧠</h3>
                      <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
                          Every event is scored dynamically for each user based on a <strong>40/40/20 weighted formula</strong>. 
                          The goal is to ensure users see experiences that align with their tribe archetype.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          <div className="bg-white/60 p-3 rounded-xl border border-white">
                              <span className="block text-[#db8654] font-bold text-xs uppercase tracking-wider mb-1">40% Personality</span>
                              <p className="text-xs text-gray-500 italic">Matches vibe tags like "Wellness" or "Growth" to user archetypes.</p>
                          </div>
                          <div className="bg-white/60 p-3 rounded-xl border border-white">
                              <span className="block text-[#db8654] font-bold text-xs uppercase tracking-wider mb-1">40% Interests</span>
                              <p className="text-xs text-gray-500 italic">Direct match between event tags and user-selected interests.</p>
                          </div>
                          <div className="bg-white/60 p-3 rounded-xl border border-white">
                              <span className="block text-[#db8654] font-bold text-xs uppercase tracking-wider mb-1">20% Location</span>
                              <p className="text-xs text-gray-500 italic">Distance-based scoring (Local events get +20%).</p>
                          </div>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* Main Events Search & Filter */}
      <div className="flex flex-col lg:flex-row gap-4 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search your events by title or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full py-2.5 pl-10 pr-4 bg-gray-50 rounded-xl border-none focus:ring-2 focus:ring-[#db8654] transition-all"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status:</span>
            <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-gray-50 border-none rounded-xl px-4 py-2 text-sm font-bold text-gray-600 focus:ring-2 focus:ring-[#db8654]"
            >
                <option value="All">All Status</option>
                <option value="Published">Published</option>
                <option value="Pending">Pending</option>
                <option value="Draft">Draft</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Source:</span>
            <select 
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="bg-gray-50 border-none rounded-xl px-4 py-2 text-sm font-bold text-gray-600 focus:ring-2 focus:ring-[#db8654]"
            >
                <option value="All">All Sources</option>
                <option value="Tribe25">Tribe25 (Manual)</option>
                <option value="Ticketmaster">Ticketmaster</option>
                <option value="Eventbrite">Eventbrite</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Target Personality:</span>
            <select 
                value={personalityFilter}
                onChange={(e) => setPersonalityFilter(e.target.value)}
                className="bg-gray-50 border-none rounded-xl px-4 py-2 text-sm font-bold text-gray-600 focus:ring-2 focus:ring-[#db8654]"
            >
                {Object.keys(PERSONALITY_VIBES).map(p => (
                    <option key={p} value={p}>{p}</option>
                ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 p-1 bg-gray-100 w-fit rounded-xl border border-gray-200">
        <button
          onClick={() => setActiveListTab("Upcoming")}
          className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeListTab === "Upcoming" ? 'bg-white text-[#db8654] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Upcoming
        </button>
        <button
          onClick={() => setActiveListTab("Ongoing")}
          className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeListTab === "Ongoing" ? 'bg-white text-[#db8654] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Ongoing
        </button>
        <button
          onClick={() => setActiveListTab("History")}
          className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${activeListTab === "History" ? 'bg-white text-[#db8654] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          History (Past)
        </button>
      </div>

      {/* External Events Discovery Section/Modal */}
      {showExternal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#FFF9F2] w-[80vw] max-w-[80vw] max-h-[90vh] rounded-3xl shadow-2xl overflow-hidden flex flex-col border border-indigo-100">
            <div className={`p-6 border-b bg-white flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <div className={`${searchSource === 'Eventbrite' ? 'bg-orange-100 text-orange-600' : 'bg-indigo-100 text-indigo-600'} p-2 rounded-xl`}>
                  <Globe size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{searchSource} Events Discovery</h3>
                  <p className="text-sm text-gray-500">Fetch and import events from {searchSource}</p>
                </div>
              </div>
              <button onClick={() => setShowExternal(false)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X size={24} />
              </button>
            </div>

            <div className={`p-4 ${searchSource === 'Eventbrite' ? 'bg-orange-50/50' : 'bg-indigo-50/50'} border-b space-y-4`}>
              <div className="flex flex-wrap gap-3">
                {searchSource === 'Ticketmaster' && (
                  <>
                    <div className="flex-1 min-w-[200px] relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                        type="text"
                        placeholder="Search city (e.g. London, Newark)..."
                        value={searchLocation}
                        onChange={(e) => setSearchLocation(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border-gray-200 focus:ring-2 focus:ring-indigo-500 transition shadow-sm text-sm"
                        />
                    </div>
                    <div className="flex-1 min-w-[200px] relative">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                        type="text"
                        placeholder="Category (e.g. yoga, music)..."
                        value={searchCategory}
                        onChange={(e) => setSearchCategory(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl border-gray-200 focus:ring-2 focus:ring-indigo-500 transition shadow-sm text-sm"
                        />
                    </div>
                  </>
                )}
                {searchSource === 'Eventbrite' && (
                  <div className="flex-1 bg-white/50 p-3 rounded-xl border border-orange-100">
                    <p className="text-xs text-orange-700 font-medium flex items-center gap-2">
                      <Info size={14} />
                      Eventbrite fetching is currently limited to your personal organization events. Filters like location and category are managed within your Eventbrite dashboard.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-4">
                    {searchSource === 'Ticketmaster' && (
                      <>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-gray-500 uppercase">Cost:</span>
                            <select 
                                value={searchPrice}
                                onChange={(e) => setSearchPrice(e.target.value)}
                                className="bg-white border-gray-200 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-600 focus:ring-2 focus:ring-indigo-500 shadow-sm"
                            >
                                <option value="All">All Prices</option>
                                <option value="Free">Free Only</option>
                                <option value="Paid">Paid Only</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-gray-500 uppercase">Type:</span>
                            <select 
                                value={searchType}
                                onChange={(e) => setSearchType(e.target.value)}
                                className="bg-white border-gray-200 rounded-lg px-3 py-1.5 text-xs font-bold text-gray-600 focus:ring-2 focus:ring-indigo-500 shadow-sm"
                            >
                                <option value="All">All Types</option>
                                <option value="In-Person">Physical</option>
                                <option value="Virtual">Online</option>
                            </select>
                        </div>
                      </>
                    )}
                </div>
                <button
                    onClick={() => fetchExternal()}
                    disabled={fetchingExternal}
                    className={`${searchSource === 'Eventbrite' ? 'bg-orange-600 hover:bg-orange-700 shadow-orange-100' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100'} text-white px-8 py-2.5 rounded-xl font-bold transition disabled:opacity-50 shadow-md text-sm`}
                >
                    {fetchingExternal ? "Searching..." : `Fetch ${searchSource} Events`}
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {fetchingExternal ? (
                <div className="h-64 flex flex-col items-center justify-center gap-4">
                  <div className={`w-12 h-12 border-4 ${searchSource === 'Eventbrite' ? 'border-orange-200 border-t-orange-600' : 'border-indigo-200 border-t-indigo-600'} rounded-full animate-spin`}></div>
                  <p className="text-gray-500 font-medium">Scanning {searchSource} for vibes...</p>
                </div>
              ) : externalEvents.filter(e => e.source === searchSource).length > 0 ? (
                <>
                <div className={`flex justify-between items-center mb-6 bg-white p-4 rounded-2xl border ${searchSource === 'Eventbrite' ? 'border-orange-100' : 'border-indigo-100'} shadow-sm sticky top-0 z-10`}>
                    <div className="flex items-center gap-3">
                        <div className={`${searchSource === 'Eventbrite' ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'} px-4 py-2 rounded-xl text-sm font-bold border`}>
                            {selectedExternal.filter(e => e.source === searchSource).length} Events Selected
                        </div>
                        {selectedExternal.filter(e => e.source === searchSource).length > 0 && (
                            <button 
                                onClick={() => setSelectedExternal(prev => prev.filter(e => e.source !== searchSource))}
                                className="text-gray-400 text-xs hover:text-red-500 font-bold"
                            >
                                Clear Selection
                            </button>
                        )}
                    </div>
                    <button
                        onClick={handleBulkPublish}
                        disabled={selectedExternal.filter(e => e.source === searchSource).length === 0 || isBulkPublishing}
                        className={`${searchSource === 'Eventbrite' ? 'bg-orange-600 hover:bg-orange-700 shadow-orange-200' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200'} text-white px-8 py-2.5 rounded-xl font-bold transition disabled:opacity-50 shadow-lg flex items-center gap-2`}
                    >
                        {isBulkPublishing ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                Processing...
                            </>
                        ) : (
                            <>
                                <CheckCircle size={18} />
                                Sync/Publish ({selectedExternal.filter(e => e.source === searchSource).length})
                            </>
                        )}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-20">
                  {externalEvents
                    .filter(e => e.source === searchSource)
                    .map((event, idx) => {
                    const isSelected = selectedExternal.find(e => e.externalId === event.externalId);
                    const published = isAlreadyPublished(event);
                    
                    return (
                        <div 
                            key={idx} 
                            onClick={() => toggleExternalSelection(event)}
                            className={`bg-white rounded-2xl border-2 transition-all cursor-pointer group relative overflow-hidden flex flex-col ${isSelected ? (searchSource === 'Eventbrite' ? 'border-orange-500 shadow-lg shadow-orange-100 ring-4 ring-orange-50' : 'border-indigo-500 shadow-lg shadow-indigo-100 ring-4 ring-indigo-50') : 'border-gray-100 shadow-sm hover:border-indigo-200'}`}
                        >
                        {isSelected && (
                            <div className={`absolute top-4 left-4 z-20 ${searchSource === 'Eventbrite' ? 'bg-orange-500' : 'bg-indigo-500'} text-white p-1.5 rounded-full shadow-lg`}>
                                <CheckCircle size={18} />
                            </div>
                        )}
                        <div className="h-40 relative">
                            <img src={event.coverImageUrl || "https://via.placeholder.com/400x200?text=Event+Image"} className="w-full h-full object-cover" />
                            <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                                <div className={`${searchSource === 'Eventbrite' ? 'bg-orange-600/90' : 'bg-indigo-600/90'} backdrop-blur-sm text-white px-2 py-1 rounded-lg text-[10px] font-bold uppercase`}>
                                    {event.source}
                                </div>
                                {published && (
                                    <div className="bg-green-600/90 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-[10px] font-bold uppercase flex items-center gap-1">
                                        <CheckCircle size={10} /> Published
                                    </div>
                                )}
                                <div className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase shadow-sm ${event.price > 0 ? 'bg-amber-500 text-white' : 'bg-green-500 text-white'}`}>
                                    {event.price > 0 ? `$${event.price}` : 'FREE'}
                                </div>
                            </div>
                        </div>
                        <div className="p-4 flex-1">
                            <h4 className="font-bold text-gray-800 line-clamp-1">{event.title}</h4>
                            <p className="text-xs text-gray-500 mt-1 mb-3 line-clamp-2">{event.description}</p>
                            
                            {searchSource === 'Eventbrite' && event.capacity > 0 && (
                                <div className="mb-3 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                    <div className="flex justify-between text-[10px] font-bold text-gray-500 uppercase mb-1">
                                        <span>Bookings</span>
                                        <span>{event.totalSold} / {event.capacity}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
                                        <div 
                                            className="bg-orange-500 h-full transition-all" 
                                            style={{ width: `${Math.min(100, (event.totalSold / event.capacity) * 100)}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-1.5 mb-4">
                            <div className="flex items-center gap-2 text-[11px] text-gray-600">
                                <Calendar size={12} className={searchSource === 'Eventbrite' ? 'text-orange-500' : 'text-indigo-500'} />
                                <span>{new Date(event.eventStartAt).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-gray-600">
                                <MapPin size={12} className={searchSource === 'Eventbrite' ? 'text-orange-500' : 'text-indigo-500'} />
                                <span className="line-clamp-1">{event.locationName}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-gray-600 font-medium">
                                <Globe size={12} className={searchSource === 'Eventbrite' ? 'text-orange-500' : 'text-indigo-500'} />
                                <span>{event.eventType}</span>
                            </div>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-auto">
                                {event.suggestedVibeTags?.map((tag, i) => (
                                    <span key={i} className={`${searchSource === 'Eventbrite' ? 'bg-orange-50 text-orange-600' : 'bg-indigo-50 text-indigo-600'} px-2 py-0.5 rounded text-[9px] font-bold uppercase`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                        </div>
                    );
                  })}
                </div>
                </>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center text-gray-400">
                  <Search size={48} className="mb-4 opacity-20" />
                  <p>No {searchSource} events found. Click "Fetch Events" to search.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {finalSortedEvents.map((event) => (
          <div key={event.id} className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="relative h-48">
              <img
                src={ImgURL(event.coverImageUrl) || "https://via.placeholder.com/400x200?text=No+Image"}
                alt={event.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm ${
                  event.status === 'Published' ? 'bg-green-100 text-green-700' : 
                  event.status === 'Pending' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {event.status}
                </span>
                
                {event.eventType !== 'External' && (
                  <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm flex items-center gap-1 ${
                    event.eventType === 'Eventbrite' ? 'bg-orange-600 text-white' : 
                    event.eventType === 'Ticketmaster' ? 'bg-indigo-600 text-white' : 'bg-white/90 backdrop-blur-sm text-[#db8654]'
                  }`}>
                    {(event.eventType === 'Eventbrite' || event.eventType === 'Ticketmaster') && <Globe size={10} />}
                    {event.eventType}
                  </span>
                )}
              </div>
            </div>

            <div className="p-5 flex-1 flex flex-col">
              <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1">{event.title}</h3>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar size={14} className="text-[#db8654]" />
                  <span>{new Date(event.eventStartAt).toLocaleDateString()} at {new Date(event.eventStartAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin size={14} className="text-[#db8654]" />
                  <span className="line-clamp-1">{event.locationName || "TBD"}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <DollarSign size={14} className="text-[#db8654]" />
                  <span>{event.price > 0 ? `$${event.price}` : "Free"}</span>
                </div>
                
                {event.source === 'Eventbrite' && event.capacity > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-50">
                    <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase mb-1">
                      <span>Eventbrite Sales</span>
                      <span className="text-orange-600">{event.totalSold} / {event.capacity}</span>
                    </div>
                    <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                      <div 
                        className="bg-orange-500 h-full transition-all" 
                        style={{ width: `${Math.min(100, (event.totalSold / event.capacity) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-1 mb-4">
                {event.vibeTags?.map((tag, i) => (
                  <span key={i} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-auto pt-4 border-t flex items-center justify-between gap-2">
                <div className="flex gap-1 items-center">
                  <Link
                    to={`/admin/events/attendees/${event.id}`}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition flex items-center gap-1.5"
                    title="View Attendees"
                  >
                    <Users size={18} />
                    <span className="text-xs font-bold">{event.attendeeCount || 0}</span>
                  </Link>
                  <Link
                    to={`/admin/events/edit/${event.id}`}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                    title="Edit"
                  >
                    <Edit size={18} />
                  </Link>
                  <button
                    onClick={() => handleDelete(event.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                {event.status === 'Pending' && (
                  <button
                    onClick={() => handleApprove(event.id)}
                    className="flex items-center gap-1 bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 transition text-sm font-medium shadow-sm"
                  >
                    <CheckCircle size={16} />
                    Approve
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {events.length === 0 && !loading && (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-dashed border-gray-300">
          <Clock size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No events found</h3>
          <p className="text-gray-500 mt-1">Start by creating a new curated event.</p>
        </div>
      )}
    </div>
  );
};

export default EventManager;
